import * as React from 'react';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import FilterBarItemComponent from './DisplayComponents/FilterBarItemComponent';
import { BarItemModel } from './DisplayComponents/ViewModels/BarItemModel';
import { FilterFlags } from './DisplayComponents/ViewModels/FilterFlags';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CallTableViewModel, { CallTableColumns } from '../ViewModels/CallTableViewModel';
import SalesFiltersViewModel from './ReworkedFilters/SalesFilters/ViewModels/SalesFiltersViewModel';
import QaFiltersViewModel from './ReworkedFilters/QaFilters/ViewModels/QaFiltersViewModel';
import OperationsFiltersViewModel from './ReworkedFilters/OperationsFilters/ViewModels/OperationsFiltersViewModel';
import VulnerabilitiesFiltersViewModel from './ReworkedFilters/VulnerabilitiesFilters/ViewModels/VulnerabilitiesFiltersViewModel';
import ComplaintsFiltersViewModel from './ReworkedFilters/ComplaintsFilters/ViewModels/ComplaintsFiltersViewModel';
import ReportCodeFiltersViewModel from './ReworkedFilters/ReportCodeFilters/ViewModels/ReportCodeFiltersViewModel';
import CallFiltersViewModel from './ReworkedFilters/CallFilters/ViewModels/CallFiltersViewModel';
import SentimentFiltersViewModel from './ReworkedFilters/SentimentFilters/ViewModels/SentimentFiltersViewModel';
import AdvancedFiltersViewModel from './ReworkedFilters/AdvancedFilters/Viewmodels/AdvancedFiltersViewModel';
import SalesAIAnswerViewModel from './ReworkedFilters/SalesAIFilters/ViewModels/SalesAIAnswerViewModel';
import InsightAnswerViewModel from './ReworkedFilters/InsightFilters/ViewModels/InsightAnswerViewModel';
import ScorecardAnswersViewModel from './ReworkedFilters/ScorecardAnswerFilters/ViewModels/ScorecardAnswersViewModel';
import QAScorecardFilterViewModel from './ReworkedFilters/QAScorecardFilter/ViewModels/QAScorecardFilterViewModel';
import AIFlaggedIssueViewModel from './ReworkedFilters/AIFlaggedIssuesFilters/ViewModels/AIFlaggedIssueViewModel';
import CustomerFiltersViewModel from './ReworkedFilters/CustomerFilter/ViewModels/CustomerFilterViewModel';
import GdprFiltersViewModel from './ReworkedFilters/VoiceOfTheCustomerFilter/ViewModel/GDPRFiltersViewModel';
import VOCFiltersViewModel from './ReworkedFilters/VoiceOfTheCustomerFilter/ViewModel/VOCFiltersViewModel';
import PresetSelector from './ReworkedFilters/Presets/PresetSelector';
import { ColumnViewModel } from './DisplayComponents/ViewModels/ColumnViewModel';
import { useSnackbar } from 'notistack';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import Tooltip from '@mui/material/Tooltip';
import TabContext from '@mui/lab/TabContext';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import QAComplaintsFiltersViewModel from './ReworkedFilters/ComplaintsFilters/ViewModels/QAComplaintsFiltersViewModel';
import AINoteFiltersViewModel from './ReworkedFilters/AINoteFilters/ViewModels/AINoteFiltersViewModel';
import Typography from '@mui/material/Typography';
import CustomerSentimentFiltersViewModel from './ReworkedFilters/SentimentFilters/ViewModels/CustomerSentimentFiltersViewModel';
import CXAIFiltersViewModel from './ReworkedFilters/CXAIFilters/ViewModels/CXAIFiltersViewModel';

interface IFilterSelectModalProps {
    isRTM: boolean;
    isClient: boolean;
    isClientAIEnabled: boolean;
    isQA: boolean;
    isAdmin: boolean;
    isAgent: boolean;
    isReport: boolean;
    callTableViewModel: CallTableViewModel;
    setCallTableViewModel: (value: CallTableViewModel) => void;
}

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export default function FilterSelectModal(props: IFilterSelectModalProps) {
    const columnLimit: number = 12;

    const { enqueueSnackbar } = useSnackbar();
    const [open, setOpen] = React.useState(false);
    const [tab, setTab] = React.useState("1");

    const [filterSearch, setFilterSearch] = React.useState<string>("");

    const [filterFlags, setFilterFlags] = React.useState(new FilterFlags());
    const [unmodifiedFilterFlags, setUnmodifiedFilterFlags] = React.useState(new FilterFlags());

    const [filterItems, setFilterItems] = React.useState<BarItemModel[]>(new Array<BarItemModel>());
    const [unmodifiedFilterItems, setUnmodifiedFilterItems] = React.useState<BarItemModel[]>(new Array<BarItemModel>());

    const [columnOptions, setColumnOptions] = React.useState<ColumnViewModel[]>([]);


    //reset the matching filters on open
    React.useEffect(() => {
        if (open) {
            ResetFilters();
        }
    }, [open])

    React.useEffect(() => {
        setFilterFlags(GenerateFilterFlags());
        setFilterItems(GenerateItemList());
    }, [props.callTableViewModel, props.isClient, props.isClientAIEnabled])

    //Set the flag model (display filters) to match the previously selected filter values
    function GenerateFilterFlags() {
        const filterFlags = new FilterFlags();
        for (let propertyName in props.callTableViewModel.filters) {
            if (typeof props.callTableViewModel.filters[propertyName] === 'object') { //for the filter models
                const filter = props.callTableViewModel.filters[propertyName];
                filterFlags[propertyName] = filter.isActive;
            }
            else if (typeof props.callTableViewModel.filters[propertyName] === 'boolean') //for the voice of the customer property
                filterFlags[propertyName] = props.callTableViewModel.filters[propertyName];
        }
        return filterFlags
    }

    function ResetFilters() {
        //reset the column filters to match the larger view model
        GenerateColumnDefaults();
        //set the tab back to the custom filter section to usability << you would never need to select a new preset after just selecting one
        setTab("1");
        //set the current state of the 
        setUnmodifiedFilterFlags({ ...filterFlags });
        //IF YOU REPLACE THIS WITH SPREADING RATHER THAN A JSON DEEP COPY IT WILL CASUE A .MAP ERROR ON CLOSE
        setUnmodifiedFilterItems(JSON.parse(JSON.stringify(filterItems)));
    }

    function Close() {
        setOpen(false);
        setFilterFlags(unmodifiedFilterFlags);
        setFilterItems(unmodifiedFilterItems);
    }

    //Assign the model's open state to the big model HERE
    function Confirm() {
        //check there is at least one column to display
        if (columnOptions.find(f => f.isActive === true) !== undefined) {
            const newCallTableViewModel = { ...props.callTableViewModel };

            //#region filter selection
            filterFlags.salesFilters
                ? newCallTableViewModel.filters.salesFilters.isActive = true
                : newCallTableViewModel.filters.salesFilters = new SalesFiltersViewModel()

            filterFlags.qaFilters
                ? newCallTableViewModel.filters.qaFilters.isActive = true
                : newCallTableViewModel.filters.qaFilters = new QaFiltersViewModel()

            filterFlags.operationsFilters
                ? newCallTableViewModel.filters.operationsFilters.isActive = true
                : newCallTableViewModel.filters.operationsFilters = new OperationsFiltersViewModel()

            filterFlags.vulnerabilitiesFilters
                ? newCallTableViewModel.filters.vulnerabilitiesFilters.isActive = true
                : newCallTableViewModel.filters.vulnerabilitiesFilters = new VulnerabilitiesFiltersViewModel()

            filterFlags.complaintsFilters
                ? newCallTableViewModel.filters.complaintsFilters.isActive = true
                : newCallTableViewModel.filters.complaintsFilters = new ComplaintsFiltersViewModel()

            filterFlags.qaComplaintsFilters
                ? newCallTableViewModel.filters.qaComplaintsFilters.isActive = true
                : newCallTableViewModel.filters.qaComplaintsFilters = new QAComplaintsFiltersViewModel()

            filterFlags.reportCodeFilters
                ? newCallTableViewModel.filters.reportCodeFilters.isActive = true
                : newCallTableViewModel.filters.reportCodeFilters = new ReportCodeFiltersViewModel()

            filterFlags.callFilters
                ? newCallTableViewModel.filters.callFilters.isActive = true
                : newCallTableViewModel.filters.callFilters = new CallFiltersViewModel()

            filterFlags.customerSentimentFilters
                ? newCallTableViewModel.filters.customerSentimentFilters.isActive = true
                : newCallTableViewModel.filters.customerSentimentFilters = new CustomerSentimentFiltersViewModel()

            filterFlags.agentSentimentFilters
                ? newCallTableViewModel.filters.agentSentimentFilters.isActive = true
                : newCallTableViewModel.filters.agentSentimentFilters = new SentimentFiltersViewModel()

            filterFlags.advancedFilters
                ? newCallTableViewModel.filters.advancedFilters.isActive = true
                : newCallTableViewModel.filters.advancedFilters = new AdvancedFiltersViewModel()

            filterFlags.customerFilters
                ? newCallTableViewModel.filters.customerFilters.isActive = true
                : newCallTableViewModel.filters.customerFilters = new CustomerFiltersViewModel()

            filterFlags.vocFilters
                ? newCallTableViewModel.filters.vocFilters.isActive = true
                : newCallTableViewModel.filters.vocFilters = new VOCFiltersViewModel()

            filterFlags.gdprFilters
                ? newCallTableViewModel.filters.gdprFilters.isActive = true
                : newCallTableViewModel.filters.gdprFilters = new GdprFiltersViewModel();

            filterFlags.aiFlaggedIssues
                ? newCallTableViewModel.filters.aiFlaggedIssues.isActive = true
                : newCallTableViewModel.filters.aiFlaggedIssues = new AIFlaggedIssueViewModel();

            filterFlags.aiNoteFilters
                ? newCallTableViewModel.filters.aiNoteFilters.isActive = true
                : newCallTableViewModel.filters.aiNoteFilters = new AINoteFiltersViewModel();

            filterFlags.salesAI
                ? newCallTableViewModel.filters.salesAI.isActive = true
                : newCallTableViewModel.filters.salesAI = new SalesAIAnswerViewModel();

            filterFlags.cxaiFilters
                ? newCallTableViewModel.filters.cxaiFilters.isActive = true
                : newCallTableViewModel.filters.cxaiFilters = new CXAIFiltersViewModel();

            filterFlags.insight
                ? newCallTableViewModel.filters.insight.isActive = true
                : newCallTableViewModel.filters.insight = new InsightAnswerViewModel();

            filterFlags.scorecardAnswer
                ? newCallTableViewModel.filters.scorecardAnswer.isActive = true
                : newCallTableViewModel.filters.scorecardAnswer = new ScorecardAnswersViewModel();

            filterFlags.qaScorecardFilter
                ? newCallTableViewModel.filters.qaScorecardFilter.isActive = true
                : newCallTableViewModel.filters.qaScorecardFilter = new QAScorecardFilterViewModel();
            //#endregion

            //#region column selection
            let newColumns: CallTableColumns = new CallTableColumns();
            for (let i in newColumns) { // set the local model to its matching CallTableColumns property
                newColumns[i] = columnOptions.find(c => c.name == newColumns[i].name) ?? { ...newColumns[i], isActive: false } // AI columns are filtered out if user doesn't have permission and need to be defaulted to inactive
            }

            //save the values to the larger model
            newCallTableViewModel.columns = newColumns;

            //#endregion

            props.setCallTableViewModel({
                ...newCallTableViewModel
            })

            setOpen(false);
        }
        else {
            displayWarning("You must select at least one column");
        }
    }

    function displayWarning(displayText: string) {
        enqueueSnackbar(displayText, { variant: 'warning' });
    }

    //create the dispaly cards that show the filter state and headings, If a filter is renamed THIS MUST ALSO REFLECT THE RENAME
    function GenerateItemList(): BarItemModel[] {
        let localList: BarItemModel[] = new Array<BarItemModel>();

        localList.push(new BarItemModel(
            "Sales", ["Sale", "Sale Type", "Product Type", "Product", "Product Offer", "Product Reference"], "You can use these filters to find calls based sales and product", "salesFilters", props.callTableViewModel.filters.salesFilters.isActive
        ));
        localList.push(new BarItemModel(
            "Call", ["Result", "Result Source", "Call Length", "System ID"], "You can use these filters to find calls based on details of the call itself", "callFilters", props.callTableViewModel.filters.callFilters.isActive
        ));
        localList.push(new BarItemModel(
            "Customer", ["CustomerID", "Telephone", "Client Ref", "URN"], "You can use these filters to find calls based on the customer on them", "customerFilters", props.callTableViewModel.filters.customerFilters.isActive
        ));
        if (!props.isClient || props.isClientAIEnabled) {
            localList.push(new BarItemModel(
                "QA", ["Monitoring Status", "Monitored By", "Monitoring Result", "Monitoring ResQ Result", "Alert"], "You can use these filters to find calls based on QA who monitored the call", "qaFilters", props.callTableViewModel.filters.qaFilters.isActive
            ));
        }

        if (!props.isAgent) {
            localList.push(new BarItemModel(
                "Operations", ["Site", "Channel", "Team", "Team Leader", "Agent name"], "You can use these filters to find calls based on agent who made them and where they are within the business", "operationsFilters", props.callTableViewModel.filters.operationsFilters.isActive
            ));
            localList.push(new BarItemModel(
                "Reportcode", [], "You can use these filters to find calls based on what dataset the call came from based on its Reportcodes", "reportCodeFilters", props.callTableViewModel.filters.reportCodeFilters.isActive
            ));
            if (!props.isClient || props.isClientAIEnabled) {
                localList.push(new BarItemModel(
                    "Voice of the Customer", [], "You can use this filter to find all of the calls that have been processed by AI", "vocFilters", props.callTableViewModel.filters.vocFilters.isActive
                ));
                localList.push(new BarItemModel(
                    "PII / GDPR", [], "You can use these filters to find calls based on the results of AI analysis highlighting any relevant calls regarding PII and GDPR", "gdprFilters", props.callTableViewModel.filters.gdprFilters.isActive
                ));
                localList.push(new BarItemModel(
                    "Sales-AI", [], "You can use these filters to find calls based on the results of AI analysis of sales performance", "salesAI", props.callTableViewModel.filters.salesAI.isActive
                ));
                localList.push(new BarItemModel(
                    "CX-AI", [], "You can use these filters to find calls based on the results of AI analysis of GUIDE", "cxaiFilters", props.callTableViewModel.filters.cxaiFilters.isActive
                ));
                localList.push(new BarItemModel(
                    "Insight", [], "You can use these filters to find calls based on the results of AI analysis of insights", "insight", props.callTableViewModel.filters.insight.isActive
                ));
                localList.push(new BarItemModel(
                    "Customer Sentiment", ["Customer Speaker", "Mode(Start/end)", "Sentiment range selection"], "You can use these filters to find calls based on the results of AI analysis of customer attitudes", "customerSentimentFilters", props.callTableViewModel.filters.customerSentimentFilters.isActive
                ));
                localList.push(new BarItemModel(
                    "Agent Sentiment", ["Agent Speaker", "Mode(Start/end)", "Sentiment range selection"], "You can use these filters to find calls based on the results of AI analysis of agent attitudes", "agentSentimentFilters", props.callTableViewModel.filters.agentSentimentFilters.isActive
                ));
                localList.push(new BarItemModel(
                    "Vulnerabilities", ["Vulnerability Category", "Vulnerability"], "You can use these filters to find calls based on the results of AI analysis on vulnerability, for example whether there was a Vulnerability identified", "vulnerabilitiesFilters", props.callTableViewModel.filters.vulnerabilitiesFilters.isActive
                ));
                localList.push(new BarItemModel(
                    "Complaints", ["Complaint Category", "Complaint"], "You can use these filters to find calls based on the results of AI analysis on complaints, for example whether there was a Complaint identified and what the Complaint Category of it was", "complaintsFilters", props.callTableViewModel.filters.complaintsFilters.isActive
                ));
                localList.push(new BarItemModel(
                    "QA Actions", ["Action Type", "Category", "Originator", "Outcome"], "You can use these filters to find calls that have been flagged for QA action such as having complaints, GDPR requests, or require a redress", "qaComplaintsFilters", props.callTableViewModel.filters.qaComplaintsFilters.isActive
                ));
                localList.push(new BarItemModel(
                    "QA Scorecard Answers", [], "You can use these filters to find calls based on the results of specific questions on a QA scorecard", "scorecardAnswer", props.callTableViewModel.filters.scorecardAnswer.isActive
                ));
                localList.push(new BarItemModel(
                    "QA Scorecard Filter", ["Template"], "You can use this filter to find calls that were monitored using a specific QA scorecard template", "qaScorecardFilter", props.callTableViewModel.filters.qaScorecardFilter.isActive
                ));
                localList.push(new BarItemModel(
                    "QA-I Flagged Issues", [], "You can use these filters to find calls based on the results of AI analysis identifying issues", "aiFlaggedIssues", props.callTableViewModel.filters.aiFlaggedIssues.isActive
                ));
                localList.push(new BarItemModel(
                    "AI Client Notes", [], "If client notes have been generated by AI, this filter can be used to report on usage by the agents", "aiNoteFilters", props.callTableViewModel.filters.aiNoteFilters.isActive
                ));
            }
            localList.push(new BarItemModel(
                "Advanced", ["Random selection", "Random per agent", "Tags include", "Unflagged NC"], "These filters are designed to help with specific queries and tasks", "advancedFilters", props.callTableViewModel.filters.advancedFilters.isActive
            ));
        }

        return localList;
    }

    //Set column Options to match the values of the callTableViewModel.columns model
    function GenerateColumnList(): ColumnViewModel[] {
        var localList: ColumnViewModel[] = [];

        //convert the CallTableColumn Object into a list for display
        for (let i in props.callTableViewModel.columns) {
            if (!props.callTableViewModel.columns[i].isHidden)//add all columns in the large model into the options list based on user mode
                localList.push(new ColumnViewModel(
                    props.callTableViewModel.columns[i].name,
                    props.callTableViewModel.columns[i].isActive)
                );
        }

        return localList;
    }

    function UpdateFilterFlag(propertyName: string) {
        try {
            setFilterFlags({
                ...filterFlags,
                [propertyName]: !filterFlags[propertyName]
            });

            let foundItem = filterItems.find(item => item.filterName === propertyName);

            if (foundItem != undefined) //swap the states
                foundItem.displaySelected = !foundItem.displaySelected;
        } catch (e) {
            console.log(e);
        }
    }

    function UpdateList(selectedList: ColumnViewModel[]) {
        var localList: ColumnViewModel[] = [];

        //selected list only contains the isActive = true values, create a new list that has all the options for display
        for (let i in props.callTableViewModel.columns) {
            if (!props.callTableViewModel.columns[i].isHidden) //add all columns in the large model into the options list based on user mode
                localList.push(new ColumnViewModel(
                    props.callTableViewModel.columns[i].name,
                    (selectedList.findIndex(f => f.name === props.callTableViewModel.columns[i].name) >= 0)) //if the value is contined in the selected list set it's active state to true, else to false
                );
        }
        //update the local list's state
        setColumnOptions(localList);
    }

    function GenerateColumnDefaults() {
        //reset the column filters to match the larger view model
        setColumnOptions(GenerateColumnList());
    }

    return (
        <Grid>
            <Chip
                label="Select Columns and Filters"
                clickable
                onClick={() => setOpen(true)}
                color="primary"
            />
            <Dialog
                open={open}
                onClose={Close}
                PaperProps={{
                    sx: {
                        minHeight: '90%',
                        maxHeight: '90%'
                    }
                }}
                maxWidth="lg"
                fullWidth>
                <TabContext value={tab}>
                    {
                        !props.isAgent &&
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <TabList
                                variant="fullWidth"
                                indicatorColor="secondary"
                                onChange={(e, value) => setTab(value)}>
                                <Tab label="Custom Filter" value="1" />
                                <Tab label="Apply Preset" value="2" />
                            </TabList>
                        </Box>
                    }
                    <TabPanel style={{ padding: '10px', paddingTop: '0px' }} value="1" key="Custom Filter">
                        <Grid container spacing={0}>
                            {
                                !props.isAgent &&
                                <Grid container size={12} style={{ marginLeft: '5px' }}>
                                    <Grid size={12}> <Typography variant="h6" fontWeight='bold' marginTop='8px'>Select Columns</Typography> </Grid>
                                    <Grid size={12}> <Typography variant="body1">Use the menu below to choose which columns you want to display on your IQ searches</Typography> </Grid>
                                    <Grid size={11} marginTop='12px'>
                                        <Autocomplete
                                            multiple
                                            id="display_columns"
                                            limitTags={5}
                                            options={columnOptions}
                                            onChange={(event, value) => {
                                                if (value.length <= columnLimit)
                                                    UpdateList(value as ColumnViewModel[]); //set the isActive in the list
                                                else
                                                    displayWarning("Only " + `${columnLimit}` + " columns can be selected at a time");
                                            }}
                                            disableCloseOnSelect
                                            getOptionLabel={(option: ColumnViewModel) => option.name}
                                            value={columnOptions.filter(m => m.isActive) ?? [] as ColumnViewModel[]} //show only active values
                                            renderOption={(props, option, { selected }) => {
                                                const { ...optionProps } = props;
                                                return (
                                                    <li {...optionProps}>
                                                        <Checkbox
                                                            icon={icon}
                                                            checkedIcon={checkedIcon}
                                                            style={{ marginRight: 8 }}
                                                            checked={option.isActive}
                                                        />
                                                        {option.name}
                                                    </li>
                                                );
                                            }}
                                            renderInput={(params) => (
                                                <TextField {...params} label="Selected Columns" />
                                            )}
                                            sx={{ width: 'auto' }}
                                        />
                                    </Grid>
                                    <Grid size={1} style={{ textAlignLast: 'center', alignSelf: 'center' }}>
                                        <Tooltip title={<div>Undo Column Changes</div>}>
                                            <SettingsBackupRestoreIcon fontSize='large' onClick={GenerateColumnDefaults} style={{ marginTop: '5px' }} />
                                        </Tooltip>
                                    </Grid>
                                </Grid>
                            }
                            <Grid size={12} style={{ marginLeft: '5px' }}>
                                <Typography variant="h6" fontWeight='bold' marginTop='8px'>Select Filters</Typography>
                                <Typography variant="body1">Use the switches below to activate different filter groups, allowing you to search for specific calls or types of calls.
                                    You can use the search bar to find which filter group a filter is in</Typography>
                                <TextField
                                    fullWidth
                                    label="Search the filters"
                                    id="filter_search"
                                    value={filterSearch}
                                    style={{ marginBottom: "12px", marginTop: "12px" }}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                        setFilterSearch(event.target.value.toLowerCase());
                                    }}
                                />
                            </Grid>
                            {filterItems
                                .map((barItem, index) =>
                                    <React.Fragment key={`filterItem-${index}`}>
                                        {(barItem.title.toLowerCase().includes(filterSearch) ||
                                            barItem.internalfilters.filter(x =>
                                                x.toLowerCase().includes(filterSearch)).length > 0) &&
                                            <Grid size={4} key={index}>
                                                <FilterBarItemComponent
                                                    title={barItem.title}
                                                    UpdateFilterFlag={UpdateFilterFlag}
                                                    internalfilters={barItem.internalfilters}
                                                    description={barItem.description}
                                                    filterName={barItem.filterName}
                                                    displaySelected={barItem.displaySelected}
                                                />
                                            </Grid>
                                        }
                                    </React.Fragment>
                                )}
                        </Grid>
                        <DialogActions>
                            <Button onClick={Close} color="primary">Cancel</Button>
                            <Button onClick={Confirm} color="primary">Confirm</Button>
                        </DialogActions>
                    </TabPanel>
                    <TabPanel style={{ padding: '10px', paddingTop: '0px' }} value="2" key="Preset Filter">
                        <PresetSelector
                            callTableViewModel={props.callTableViewModel}
                            setCallTableViewModel={props.setCallTableViewModel}
                            close={Close}
                        />
                    </TabPanel>
                </TabContext>
            </Dialog>
        </Grid >
    );
}
